import { Form, FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import FiltersPanel from "./features/filters/FiltersPanel";
import ResultsPanel from "./features/results/ResultsPanel";
import useRulesQuery from "./hooks/useRulesQuery";
import useDiamondsInfiniteQuery from "./hooks/useDiamondsInfiniteQuery";
import useDiamondCountQuery from "./hooks/useDiamondCountQuery";
import useSessionStorageSync, { readSessionStorage } from "./hooks/useSessionStorageSync";
import { useSettings } from "./providers/SettingsProvider";

const INITIAL_STATE = {
    shape: ["ROUND"],
    color: [],
    clarity: [],
    cut: [],
    polish: [],
    symmetry: [],
    fluorescence: [],
    sort_field: "price",
    sort_order: "asc",
};

// Pick a numeric field with `??` so intentional zeros pass through. Only fall
// back when the value is null/undefined.
function num(value, fallback) {
    return value == null ? fallback : Number(value);
}

function buildDefaults(naturalRule) {
    if (!naturalRule) return INITIAL_STATE;
    const priceFrom = num(naturalRule.total_price_from, 0);
    const priceTo = num(naturalRule.total_price_to, 999999);
    const caratFrom = num(naturalRule.min_carat, 0);
    const caratTo = num(naturalRule.max_carat, 100);
    const tableFrom = num(naturalRule.tablemin, 0);
    const tableTo = num(naturalRule.tablemax, 100);
    const depthFrom = num(naturalRule.depthmin, 0);
    const depthTo = num(naturalRule.depthmax, 100);
    return {
        ...INITIAL_STATE,
        price: [priceFrom, priceTo],
        carat: [caratFrom, caratTo],
        table: [tableFrom, tableTo],
        depth: [depthFrom, depthTo],
        total_price_from: priceFrom,
        total_price_to: priceTo,
        min_carat: caratFrom,
        max_carat: caratTo,
        tablemin: tableFrom,
        tablemax: tableTo,
        depthmin: depthFrom,
        depthmax: depthTo,
    };
}

function buildSearchBody(values, userId) {
    const body = { page: 1 };
    if (userId != null) body.user_id = userId;

    const arrayKeys = [
        "search", "shape", "cut", "clarity", "color", "fancy_color",
        "fancy_overtone", "fancy_intensity", "lab", "polish", "symmetry", "fluorescence",
    ];
    arrayKeys.forEach((k) => {
        if (Array.isArray(values[k]) && values[k].length) body[k] = values[k];
    });

    const rangeKeys = [
        "min_carat", "max_carat", "total_price_from", "total_price_to",
        "depthmin", "depthmax", "tablemin", "tablemax", "ratiomin", "ratiomax",
    ];
    rangeKeys.forEach((k) => {
        const v = values[k];
        // Only send actual numbers. Excludes "", null, undefined, NaN.
        if (typeof v === "number" && !Number.isNaN(v)) body[k] = v;
    });

    body.sort_field = values.sort_field;
    body.sort_order = values.sort_order;
    return body;
}

export default function App() {
    const didSubmitOnLoad = useRef(false);
    const { settings } = useSettings();
    const { data: naturalRule, isLoading: rulesLoading } = useRulesQuery();
    const [searchBody, setSearchBody] = useState(null);

    const defaults = useMemo(() => buildDefaults(naturalRule), [naturalRule]);
    const initialValues = useMemo(
        () => readSessionStorage("filterData", defaults),
        [defaults]
    );

    const formik = useFormik({
        enableReinitialize: false,
        initialValues,
        onSubmit: (values) => {
            const merged = { ...defaults, ...values };
            setSearchBody(buildSearchBody(merged, settings?.user_id));
        },
    });

    useSessionStorageSync("filterData", formik.values);

    useEffect(() => {
        if (!didSubmitOnLoad.current && naturalRule) {
            didSubmitOnLoad.current = true;
            const merged = { ...defaults, ...formik.values };
            setSearchBody(buildSearchBody(merged, settings?.user_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [naturalRule]);

    const diamondsQuery = useDiamondsInfiniteQuery(searchBody);
    const countQuery = useDiamondCountQuery(searchBody);

    const flatData = useMemo(
        () => diamondsQuery.data?.pages.flat() ?? [],
        [diamondsQuery.data]
    );

    const handleReset = () => {
        sessionStorage.removeItem("filterData");
        formik.resetForm({ values: defaults });
        setSearchBody(buildSearchBody(defaults, settings?.user_id));
    };

    const onLoadMore = () => {
        if (diamondsQuery.hasNextPage && !diamondsQuery.isFetchingNextPage) {
            diamondsQuery.fetchNextPage();
        }
    };

    const svg = naturalRule?.svg ?? {};

    // Loading vs empty vs error - explicit state machine. `isLoading` is true
    // only on first fetch (per query keyed by filters). `flatData` is empty
    // either because the fetch hasn't returned or returned 0 rows. Differentiate.
    const isInitialLoad = diamondsQuery.isLoading;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
                <FiltersPanel
                    loading={rulesLoading}
                    handleReset={handleReset}
                    naturalRule={naturalRule}
                    svg={svg}
                />
                <ResultsPanel
                    naturalData={flatData}
                    loading={isInitialLoad}
                    isError={diamondsQuery.isError}
                    error={diamondsQuery.error}
                    diamondcount={countQuery.data}
                    countLoading={countQuery.isLoading}
                    onLoadMore={onLoadMore}
                    moreLoading={diamondsQuery.isFetchingNextPage}
                />
            </Form>
        </FormikProvider>
    );
}
